import React from 'react';
import {
  BarChart3,
  Calendar,
  CalendarDays,
  Coins,
  Gamepad2,
  Layers,
  Lock,
  Percent,
  Swords,
  TrendingUp,
} from 'lucide-react';
import type { ConsumerVolumeOverview } from 'types/admin';

export const EMPTY_VOLUME_OVERVIEW: ConsumerVolumeOverview = {
  battlesPlayed: 0,
  battlesToday: 0,
  totalVolume: 0,
  volumeToday: 0,
  totalCommission: 0,
  commissionToday: 0,
  totalDistribute: 0,
  distributeToday: 0,
};

export const formatVolumeMoney = (value: number, symbol = '') =>
  `${symbol}${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function mergeVolumeOverviews(
  a: ConsumerVolumeOverview,
  b: ConsumerVolumeOverview
): ConsumerVolumeOverview {
  const left = { ...EMPTY_VOLUME_OVERVIEW, ...a };
  const right = { ...EMPTY_VOLUME_OVERVIEW, ...b };
  return {
    battlesPlayed: left.battlesPlayed + right.battlesPlayed,
    battlesToday: left.battlesToday + right.battlesToday,
    totalVolume: left.totalVolume + right.totalVolume,
    volumeToday: left.volumeToday + right.volumeToday,
    totalCommission: left.totalCommission + right.totalCommission,
    commissionToday: left.commissionToday + right.commissionToday,
    totalDistribute: left.totalDistribute + right.totalDistribute,
    distributeToday: left.distributeToday + right.distributeToday,
    commissionIsEstimated: Boolean(left.commissionIsEstimated || right.commissionIsEstimated),
    commissionTodayIsEstimated: Boolean(left.commissionTodayIsEstimated || right.commissionTodayIsEstimated),
    distributeIsEstimated: Boolean(left.distributeIsEstimated || right.distributeIsEstimated),
    distributeTodayIsEstimated: Boolean(left.distributeTodayIsEstimated || right.distributeTodayIsEstimated),
    houseRakePercentage: left.houseRakePercentage ?? right.houseRakePercentage,
  };
}

type StatCardProps = {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  iconWrap: string;
  compact?: boolean;
};

function StatCard({ icon: Icon, label, value, sub, iconWrap, compact }: StatCardProps) {
  return (
    <div
      className={`rounded-xl border border-line shadow-sm hover:shadow-md transition-shadow ${
        compact ? 'bg-subtle/35 p-4' : 'bg-subtle/35 p-4 sm:p-5'
      }`}
    >
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl mb-2.5 ${iconWrap}`}>
        <Icon size={18} strokeWidth={2} />
      </div>
      <p className="text-[10px] font-bold text-muted uppercase tracking-wider leading-snug">{label}</p>
      <p className={`font-extrabold text-heading mt-1 tabular-nums ${compact ? 'text-xl sm:text-2xl' : 'text-xl sm:text-2xl'}`}>
        {value}
      </p>
      {sub && <p className="text-[11px] text-muted mt-1.5 leading-snug">{sub}</p>}
    </div>
  );
}

type StatsSectionProps = {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  headerBg: string;
  iconWrap: string;
  accentBar: string;
  compact?: boolean;
  children: React.ReactNode;
};

function StatsSection({ title, subtitle, icon: Icon, headerBg, iconWrap, accentBar, compact, children }: StatsSectionProps) {
  return (
    <section className={`rounded-xl border border-line bg-card overflow-hidden ${compact ? 'shadow-sm' : ''}`}>
      <div className={`relative px-4 py-3 sm:px-5 border-b border-line ${headerBg}`}>
        <div className={`absolute inset-y-0 left-0 w-1 ${accentBar}`} aria-hidden />
        <div className="flex items-center gap-2.5 pl-2">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-black/5 ${iconWrap}`}>
            <Icon size={17} strokeWidth={2.25} />
          </div>
          <div className="min-w-0">
            <h4 className={`font-bold text-heading tracking-tight ${compact ? 'text-sm sm:text-base' : 'text-sm sm:text-base'}`}>
              {title}
            </h4>
            <p className="text-[11px] sm:text-xs text-muted mt-0.5">{subtitle}</p>
          </div>
        </div>
      </div>
      <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">{children}</div>
    </section>
  );
}

function totalStatCards(
  overview: ConsumerVolumeOverview,
  symbol: string,
  labels: { battle: string; volume: string; commission: string; distribute: string },
  compact?: boolean
) {
  const rake = overview.houseRakePercentage ?? 0;
  return (
    <>
      <StatCard compact={compact} icon={Swords} label={labels.battle} value={overview.battlesPlayed.toLocaleString()} iconWrap="bg-indigo-500/10 text-indigo-600" />
      <StatCard compact={compact} icon={TrendingUp} label={labels.volume} value={formatVolumeMoney(overview.totalVolume, symbol)} iconWrap="bg-violet-500/10 text-violet-600" />
      <StatCard compact={compact} icon={Percent} label={labels.commission} value={formatVolumeMoney(overview.totalCommission, symbol)} sub={overview.commissionIsEstimated ? `Estimated at ${rake}% rake` : undefined} iconWrap="bg-rose-500/10 text-rose-700" />
      <StatCard compact={compact} icon={Coins} label={labels.distribute} value={formatVolumeMoney(overview.totalDistribute, symbol)} sub={overview.distributeIsEstimated ? 'Estimated (volume − commission)' : undefined} iconWrap="bg-emerald-500/10 text-emerald-600" />
    </>
  );
}

function todayStatCards(
  overview: ConsumerVolumeOverview,
  symbol: string,
  labels: { battle: string; volume: string; commission: string; distribute: string },
  compact?: boolean
) {
  const rake = overview.houseRakePercentage ?? 0;
  return (
    <>
      <StatCard compact={compact} icon={Swords} label={labels.battle} value={overview.battlesToday.toLocaleString()} iconWrap="bg-blue-500/10 text-blue-600" />
      <StatCard compact={compact} icon={Calendar} label={labels.volume} value={formatVolumeMoney(overview.volumeToday, symbol)} iconWrap="bg-indigo-500/10 text-indigo-600" />
      <StatCard compact={compact} icon={Percent} label={labels.commission} value={formatVolumeMoney(overview.commissionToday, symbol)} sub={overview.commissionTodayIsEstimated ? `Estimated at ${rake}% rake` : undefined} iconWrap="bg-rose-500/10 text-rose-700" />
      <StatCard compact={compact} icon={Coins} label={labels.distribute} value={formatVolumeMoney(overview.distributeToday, symbol)} sub={overview.distributeTodayIsEstimated ? 'Estimated (volume − commission)' : undefined} iconWrap="bg-emerald-500/10 text-emerald-600" />
    </>
  );
}

export type BattleVolumeOverviewPanelsProps = {
  normalOverview: ConsumerVolumeOverview;
  privateOverview: ConsumerVolumeOverview;
  currencySymbol?: string;
  compact?: boolean;
  /** Wrap in outer card with header (consumer portal). */
  framed?: boolean;
  frameTitle?: string;
  frameSubtitle?: string;
  normalScopeLabel?: string;
  privateScopeLabel?: string;
  combinedScopeLabel?: string;
};

export function BattleVolumeOverviewPanels({
  normalOverview,
  privateOverview,
  currencySymbol = '',
  compact = false,
  framed = false,
  frameTitle = 'Battle overview',
  frameSubtitle = 'Combined totals plus public lobby and private-table breakdowns',
  combinedScopeLabel = 'All battles',
  normalScopeLabel = 'Public lobby',
  privateScopeLabel = 'Private tables',
}: BattleVolumeOverviewPanelsProps) {
  const normal = { ...EMPTY_VOLUME_OVERVIEW, ...normalOverview };
  const priv = { ...EMPTY_VOLUME_OVERVIEW, ...privateOverview };
  const combined = mergeVolumeOverviews(normal, priv);

  const body = (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold text-muted uppercase tracking-widest mb-3 px-1 flex items-center gap-2">
          <Layers size={14} className="text-slate-600" />
          {combinedScopeLabel}
        </p>
        <div className="space-y-4">
          <StatsSection
            compact={compact}
            title="Total overview"
            subtitle="All-time public lobby + private table battles combined"
            icon={BarChart3}
            headerBg="bg-gradient-to-r from-slate-500/8 via-card to-card"
            iconWrap="bg-slate-500/12 text-slate-700"
            accentBar="bg-slate-500"
          >
            {totalStatCards(combined, currencySymbol, {
              battle: 'Total Battles',
              volume: 'Total Volume',
              commission: 'Total Commission',
              distribute: 'Total Distribute',
            }, compact)}
          </StatsSection>

          <StatsSection
            compact={compact}
            title="Today overview"
            subtitle="Today's public lobby + private table activity combined"
            icon={CalendarDays}
            headerBg="bg-gradient-to-r from-teal-500/8 via-card to-card"
            iconWrap="bg-teal-500/12 text-teal-700"
            accentBar="bg-teal-500"
          >
            {todayStatCards(combined, currencySymbol, {
              battle: 'Today Battles',
              volume: 'Today Volume',
              commission: 'Today Commission',
              distribute: 'Today Distribute',
            }, compact)}
          </StatsSection>
        </div>
      </div>

      <div>
        <p className="text-xs font-bold text-muted uppercase tracking-widest mb-3 px-1 flex items-center gap-2">
          <Gamepad2 size={14} className="text-indigo-600" />
          {normalScopeLabel}
        </p>
        <div className="space-y-4">
          <StatsSection
            compact={compact}
            title="Public Total"
            subtitle="All-time public lobby battles — excludes private tables"
            icon={BarChart3}
            headerBg="bg-gradient-to-r from-violet-500/8 via-card to-card"
            iconWrap="bg-violet-500/12 text-violet-600"
            accentBar="bg-violet-500"
          >
            {totalStatCards(normal, currencySymbol, {
              battle: 'Total Public Battles',
              volume: 'Total Public Volume',
              commission: 'Total Public Commission',
              distribute: 'Total Public Distribute',
            }, compact)}
          </StatsSection>

          <StatsSection
            compact={compact}
            title="Public Today"
            subtitle="Public lobby activity for the current calendar day"
            icon={CalendarDays}
            headerBg="bg-gradient-to-r from-blue-500/8 via-card to-card"
            iconWrap="bg-blue-500/12 text-blue-600"
            accentBar="bg-blue-500"
          >
            {todayStatCards(normal, currencySymbol, {
              battle: 'Today Public Battles',
              volume: 'Today Public Volume',
              commission: 'Today Public Commission',
              distribute: 'Today Public Distribute',
            }, compact)}
          </StatsSection>
        </div>
      </div>

      <div>
        <p className="text-xs font-bold text-muted uppercase tracking-widest mb-3 px-1 flex items-center gap-2">
          <Lock size={14} className="text-amber-700" />
          {privateScopeLabel}
        </p>
        <div className="space-y-4">
          <StatsSection
            compact={compact}
            title="Private Total"
            subtitle="All-time private-table battles only"
            icon={Lock}
            headerBg="bg-gradient-to-r from-amber-500/8 via-card to-card"
            iconWrap="bg-amber-500/12 text-amber-700"
            accentBar="bg-amber-500"
          >
            {totalStatCards(priv, currencySymbol, {
              battle: 'Total Private Battles',
              volume: 'Total Private Volume',
              commission: 'Total Private Commission',
              distribute: 'Total Private Distribute',
            }, compact)}
          </StatsSection>

          <StatsSection
            compact={compact}
            title="Private Today"
            subtitle="Private-table activity for the current calendar day"
            icon={Lock}
            headerBg="bg-gradient-to-r from-orange-500/8 via-card to-card"
            iconWrap="bg-orange-500/12 text-orange-700"
            accentBar="bg-orange-500"
          >
            {todayStatCards(priv, currencySymbol, {
              battle: 'Today Private Battles',
              volume: 'Today Private Volume',
              commission: 'Today Private Commission',
              distribute: 'Today Private Distribute',
            }, compact)}
          </StatsSection>
        </div>
      </div>
    </div>
  );

  if (!framed) {
    return body;
  }

  return (
    <div className="rounded-2xl border border-line bg-card shadow-sm overflow-hidden">
      <div className="px-5 sm:px-6 py-5 border-b border-line bg-gradient-to-r from-indigo-500/5 via-card to-brand-gold/5">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-600">
            <BarChart3 size={22} strokeWidth={2.25} />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-heading tracking-tight">{frameTitle}</h3>
            <p className="text-sm text-muted mt-0.5">{frameSubtitle}</p>
          </div>
        </div>
      </div>
      <div className="px-4 sm:px-6 py-5">{body}</div>
    </div>
  );
}
